<template>
  <div class="details w-full">
    <CRatingFeedback
      v-if="launchDarkly.allow_rep_file_rating && !inCall && !isFileAlreadyRated && !isAdminApp"
      v-model:comment="feedbackState.comment"
      v-model:rating="feedbackState.rating"
      class="mt-2"
      :notification-duration="RATING_NOTIFICATION_DURATION"
      @submit="submitFeedback"
    />

    <div class="flex mb-[10px] mt-[34px] font-600">
      <div>
        {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.title') }}
      </div>
    </div>

    <div class="flex gap-8 lg:gap-16">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.owner') }}
          </div>
          <div>
            {{ file?.created_by.name }}
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.size') }}
          </div>
          <div v-if="file?.size">
            {{ displayBytesWithMUnit(file?.size) }}
          </div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.lastUpdate') }}
          </div>
          <div v-if="file?.modified_at" v-html="formatDateDetailed(file?.modified_at, isDetailedDateEnabled)"></div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.expiryDate') }}
          </div>
          <div v-if="file?.expires_at" v-html="formatDateDetailed(file?.expires_at, isDetailedDateEnabled)"></div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.type') }}
          </div>
          <div>
            <component :is="renderContentType(file.content_type)" v-if="file?.content_type" />
          </div>
        </div>

        <div class="flex flex-col flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.status') }}
          </div>
          <div class="flex gap-2 items-center">
            <CIcon :color="fileStatusInfo.color" fa-type="fas" icon="circle"></CIcon>
            <div>
              {{ fileStatusInfo.text }}
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.uploadedOn') }}
          </div>
          <div v-if="file?.created_at" v-html="formatDateDetailed(file?.created_at, isDetailedDateEnabled)"></div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.fileSelectionInfo.sections.details.sharing') }}
          </div>
          <div>
            {{
              file?.permissions?.can_share
                ? t('canvasUI.browserApp.fileSelectionInfo.sections.details.sharingOptions.sharingAllowed')
                : t('canvasUI.browserApp.fileSelectionInfo.sections.details.sharingOptions.sharingDisabled')
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { computed, inject, ref, type ComputedRef, type Ref } from 'vue'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CRatingFeedback from '@lib/components/CRatingFeedback/CRatingFeedback.vue'
import useFilePanel from '@lib/components/CFilePanel/CFilePanel.use'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useThemeVars from '@lib/composables/useThemeVars'
import { displayBytesWithMUnit } from '@lib/util/number'
import { formatDateDetailed } from '@lib/util/date'
import type { File } from '@openapi'
import { FileStatusEnum } from '@openapi'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { CALL_STORAGE_KEY, CallState, INITIAL_CALL_STATE, type Call } from '@lib/types/call'

const RATING_NOTIFICATION_DURATION = 3000
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const track = inject<(payload: { event_name: string; payload: any }) => void>('track', () => {
  console.warn('track is not provided')
})
const cFilePanelProps = inject<{ file?: Ref<File>; isAdmin?: Ref<boolean> }>('CFilePanelProps')
const isAdminApp = computed(() => !!cFilePanelProps?.isAdmin?.value)
const { addRatedFile, ratedFiles } = useFilePanel()
const { t } = useI18n()
const { renderContentType } = useFileDisplayHelpers()
const themeVars = useThemeVars()
const call = useLocalStorage<Call>(CALL_STORAGE_KEY, INITIAL_CALL_STATE, {
  deep: true,
  listenToStorageChanges: true,
})
const feedbackState = ref({
  comment: '',
  rating: null,
})
const file = computed(() => cFilePanelProps?.file?.value || null)

const isDetailedDateEnabled = computed(() => {
  return launchDarkly.value?.enable_detailed_date_format
})

interface FileStatusInfo {
  text: string
  color: string
}

const inCall = computed(() => call.value && call.value.callState === CallState.IN_CALL)

const fileStatusInfo: ComputedRef<FileStatusInfo> = computed(() => {
  switch (file.value?.status) {
    case FileStatusEnum.VIEWABLE:
      return {
        text: t('canvasUI.browserApp.fileSelectionInfo.sections.details.statuses.ready'),
        color: themeVars.value.success,
      }
    case FileStatusEnum.PENDING:
      return {
        text: t('canvasUI.browserApp.fileSelectionInfo.sections.details.statuses.converting'),
        color: themeVars.value.text2,
      }
    case 'error' as any:
      return {
        text: t('canvasUI.browserApp.fileSelectionInfo.sections.details.statuses.error'),
        color: themeVars.value.error,
      }

    default:
      return {
        text: t('canvasUI.browserApp.fileSelectionInfo.sections.details.statuses.unknown'),
        color: themeVars.value.text2,
      }
  }
})

const isFileAlreadyRated = computed(() => !!file.value?.id && ratedFiles.value[file.value.id])

function submitFeedback({ rating, comment }: { rating: number | null; comment: string }) {
  if (file.value?.id) {
    const fileId = file.value.id
    setTimeout(() => addRatedFile(fileId), RATING_NOTIFICATION_DURATION)
    track?.({
      event_name: CDP_EVENT_TYPE.userFileReviewed,
      payload: {
        file_id: fileId,
        rating,
        comment,
      },
    })
  }
}
</script>

<style lang="scss" scoped>
.title {
  color: v-bind('themeVars.text2');
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.15px;
}

.not-available {
  color: v-bind('themeVars.text3');
}
</style>
